import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { apiFetch } from "../utils/api";

/**
 * Auth state for the whole app, read from the server.
 *
 * `isAuthenticated` comes from /api/employee/me — which derives it from the httpOnly
 * JWT cookie the browser cannot read or forge. It is deliberately NOT a flag in
 * localStorage or a prop someone can flip in devtools: the header decides whether to
 * MOUNT the notification bell from this, and a mounted bell fetches employee data.
 *
 * Note this only governs what the UI renders. The server re-derives the viewer on
 * every request and filters rows by audience regardless of what the client believes.
 */
const AuthContext = createContext({
    employee: null,
    isAuthenticated: false,
    loading: true,
    sections: null,
    isSectionVisible: () => false,
    refresh: async () => { },
    logout: async () => { },
});

export function AuthProvider({ children }) {
    const [employee, setEmployee] = useState(null);
    const [loading, setLoading] = useState(true);

    /**
     * Which sections exist FOR THIS VIEWER (section-level visibility).
     *
     * It lives here because it is viewer-derived state with exactly the same lifecycle as
     * auth: signing in can reveal a section, so it must be re-fetched on the same trigger.
     * One provider also means the nav and the home teasers share a single request instead
     * of racing two.
     *
     * null = not yet known. Consumers must treat that as "don't render", NOT as "show
     * everything" — optimistically rendering would flash a link to a section the viewer
     * is not allowed to know exists, which is the whole thing this feature prevents.
     *
     * This is presentation only. The server 404s these sections for a visitor whether or
     * not the UI cooperates; hiding the link just avoids offering a door to a 404.
     */
    const [sections, setSections] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const res = await apiFetch("/api/employee/me");
            const data = await res.json();
            setEmployee(data?.authenticated ? data.employee : null);
        } catch (err) {
            // A visitor with no network access to the API is still a visitor.
            console.error("Could not resolve auth state:", err);
            setEmployee(null);
        } finally {
            setLoading(false);
        }
    }, []);

    const refreshSections = useCallback(async () => {
        try {
            const res = await apiFetch("/api/Dolluzcorp/sections");
            const data = await res.json();
            setSections(data?.success ? data.data : {});
        } catch (err) {
            // Unknown → show nothing section-specific, rather than guess and leak.
            console.error("Could not resolve section visibility:", err);
            setSections({});
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Re-read after auth resolves AND whenever it changes: logging in can reveal a
    // section, logging out must hide it again without a page reload.
    useEffect(() => {
        if (!loading) refreshSections();
    }, [loading, employee, refreshSections]);

    const logout = useCallback(async () => {
        try {
            await apiFetch("/api/login/logout", { method: "POST" });
        } finally {
            setEmployee(null);
        }
    }, []);

    return (
        <AuthContext.Provider
            value={{
                employee,
                setEmployee,
                isAuthenticated: !!employee,
                loading,
                sections,
                // Unknown (still loading, or the request failed) reads as NOT visible.
                isSectionVisible: (name) => !!sections && sections[name] === true,
                refresh,
                logout,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
